import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API_BASE = '/api';
const api = {
  get:  (p: string) => axios.get(API_BASE + p),
  post: (p: string, d?: any) => axios.post(API_BASE + p, d),
};

type Contact = {
  id: number; teams_user_id: string; display_name: string; email: string;
  job_title: string; department: string; mobile_phone: string;
  presence_status: string; last_synced_at: string;
};

const PRESENCE_COLORS: Record<string, string> = {
  available: '#22c55e', busy: '#ef4444', dnd: '#dc2626',
  away: '#f59e0b', in_call: '#f97316', offline: '#6b7280', unknown: '#475569',
};
const PRESENCE_LABELS: Record<string, string> = {
  available: 'Available', busy: 'Busy', dnd: 'Do Not Disturb',
  away: 'Away', in_call: 'In a Call', offline: 'Offline', unknown: 'Unknown',
};

function PresenceBadge({ status }: { status: string }) {
  const color = PRESENCE_COLORS[status] || '#475569';
  const label = PRESENCE_LABELS[status] || 'Unknown';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px',
      borderRadius: 12, background: color + '22', border: `1px solid ${color}40` }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
      <span style={{ color, fontSize: 11, fontWeight: 600 }}>{label}</span>
    </span>
  );
}

export default function TeamsContactsPage({ user }: any) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const flash = (text: string, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback((q = '') => {
    setLoading(true);
    api.get('/teams/contacts' + (q ? `?search=${encodeURIComponent(q)}` : ''))
      .then((r: any) => setContacts(r.data))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    load(e.target.value);
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r: any = await api.post('/teams/contacts/sync');
      flash(`Synced ${r.data.synced} contacts (${r.data.added} new)`);
      load(search);
    } catch (e: any) { flash(e.response?.data?.error || e.message, false); }
    finally { setSyncing(false); }
  };

  const openTeamsChat = (email: string) => {
    window.open(`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}`, '_blank');
  };

  const isAdmin = user?.is_admin || user?.role === 'super_admin' || user?.role === 'tenant_admin';

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 28 }}>👥</span>
          <div>
            <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: 22, fontWeight: 700 }}>Teams Contacts</h2>
            <p style={{ color: '#64748b', margin: '2px 0 0', fontSize: 12 }}>Microsoft directory with live presence status</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ color: '#475569', fontSize: 12 }}>{contacts.length} contacts</span>
          {isAdmin && (
            <button onClick={syncNow} disabled={syncing}
              style={{ background: syncing ? '#334155' : '#1d4ed8', color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: syncing ? 'not-allowed' : 'pointer' }}>
              {syncing ? 'Syncing...' : '⟳ Sync Now'}
            </button>
          )}
        </div>
      </div>

      {msg && (
        <div style={{ background: msg.ok ? '#14532d' : '#7f1d1d', border: `1px solid ${msg.ok ? '#22c55e' : '#ef4444'}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: msg.ok ? '#bbf7d0' : '#fecaca', fontSize: 13 }}>
          {msg.text}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <input value={search} onChange={handleSearch} placeholder="Search by name, email, or department..."
          style={{ width: '100%', maxWidth: 400, background: '#1e293b', border: '1px solid #334155',
            borderRadius: 8, color: '#f1f5f9', padding: '10px 14px', fontSize: 13, boxSizing: 'border-box' }} />
      </div>

      {loading ? (
        <div style={{ color: '#64748b', padding: 40, textAlign: 'center' }}>Loading contacts...</div>
      ) : contacts.length === 0 ? (
        <div style={{ color: '#64748b', padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>👥</div>
          <div style={{ fontSize: 15, marginBottom: 8 }}>No contacts found</div>
          <div style={{ fontSize: 12 }}>
            {isAdmin ? 'Enable contact sync in Teams Settings and click Sync Now' : 'Contact your administrator to sync Microsoft directory'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
          {contacts.map(c => (
            <div key={c.id} style={{ background: '#1e293b', borderRadius: 12, padding: '16px 20px',
              border: '1px solid #1e3a5f', transition: 'border-color 0.15s' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#6366f1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                    {(c.display_name || c.email || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{c.display_name || c.email}</div>
                    <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{c.email}</div>
                  </div>
                </div>
                <PresenceBadge status={c.presence_status || 'unknown'} />
              </div>

              {(c.job_title || c.department) && (
                <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>
                  {c.job_title}{c.job_title && c.department ? ' · ' : ''}{c.department}
                </div>
              )}

              {c.mobile_phone && (
                <div style={{ color: '#64748b', fontSize: 11, marginBottom: 10 }}>📞 {c.mobile_phone}</div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={() => openTeamsChat(c.email)}
                  style={{ flex: 1, background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6,
                    padding: '7px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  💬 Chat in Teams
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
